import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProtectedRoute = ({ children, requiredRole, requireRole = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // If user data passed from AuthCallback, skip auth check
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  const checkSubscription = async (userData) => {
    // Skip subscription check for admin users
    if (userData.is_admin || userData.email === "ebrahemgouyrr@gmail.com") return true;
    
    // Skip subscription check for users without company or during setup
    if (!userData.company_id || !userData.role) return true;
    
    try {
      const res = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
      if (res.ok) {
        const status = await res.json();
        // Block access for expired, canceled, or any status without access
        if (!status.has_access || status.status === "expired" || status.status === "canceled") {
          navigate('/paywall', { replace: true });
          return false;
        }
      }
    } catch (e) { console.error(e); }
    return true;
  };

  useEffect(() => {
    // Skip if user data already available from navigation state
    if (location.state?.user) {
      const userData = location.state.user;
      setUser(userData);
      setIsAuthenticated(true);
      
      // Handle role-based redirects
      if (requireRole && !userData.role) {
        navigate('/setup', { state: { user: userData }, replace: true });
        return;
      }
      
      if (requiredRole && userData.role !== requiredRole) {
        // Redirect to correct dashboard
        if (userData.role === 'employer') {
          navigate('/employer', { state: { user: userData }, replace: true });
        } else if (userData.role === 'employee') {
          navigate('/employee', { state: { user: userData }, replace: true });
        }
        return;
      }
      
      // Check subscription
      checkSubscription(userData);
      return;
    }

    // Server verification
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);

        // Handle role-based redirects
        if (requireRole && !userData.role) {
          navigate('/setup', { state: { user: userData }, replace: true });
          return;
        }

        if (requiredRole && userData.role !== requiredRole) {
          // Redirect to correct dashboard
          if (userData.role === 'employer') {
            navigate('/employer', { state: { user: userData }, replace: true });
          } else if (userData.role === 'employee') {
            navigate('/employee', { state: { user: userData }, replace: true });
          }
          return;
        }

        // Check subscription for employers
        await checkSubscription(userData);

      } catch (error) {
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };
    
    checkAuth();
  }, [navigate, location.state, requiredRole, requireRole]);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <div className="w-6 h-6 rounded-full bg-primary"></div>
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Authenticated - render children
  return children;
};

export default ProtectedRoute;
